import { alpha, Box, styled, Tooltip, Typography } from '@mui/material';
import { MatchData } from '@/types/matchData';
import { Asset } from '@/api/generatedApi';
import { getAssetName } from '@/utils/assets';
import { Thumbnail } from '../Thumbnail';

const MatchPercentageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const PartMatchContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
}));

const AssetBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  flex: '0 0 70px',
}));

const AssetLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.65rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  textAlign: 'center',
  wordBreak: 'break-word',
  width: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
}));

const ArrowsContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.75),
  minWidth: 0,
}));

const PercentageBar = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: 24,
  backgroundColor: theme.palette.divider,
  borderRadius: 12,
  overflow: 'visible',
  marginTop: theme.spacing(1),
}));

const PercentageFill = styled(Box, {
  shouldForwardProp: property => property !== 'hasArrow',
})<{ hasArrow?: boolean }>(({ hasArrow = true, theme }) => ({
  position: 'relative',
  height: '100%',
  backgroundColor: theme.palette.secondary.main,
  transition: 'width 0.3s ease',
  borderRadius: hasArrow ? '12px 0 0 12px' : '12px',
  ...(hasArrow && {
    '&::after': {
      content: '""',
      position: 'absolute',
      right: -12,
      top: '50%',
      transform: 'translateY(-50%)',
      width: 0,
      height: 0,
      borderLeft: `12px solid ${theme.palette.secondary.main}`,
      borderTop: '12px solid transparent',
      borderBottom: '12px solid transparent',
    },
  }),
}));

const ReversePercentageFill = styled(Box)(({ theme }) => ({
  position: 'absolute',
  right: 0,
  top: 0,
  height: '100%',
  backgroundColor: theme.palette.secondary.main,
  transition: 'width 0.3s ease',
  borderRadius: '0 12px 12px 0',
  '&::after': {
    content: '""',
    position: 'absolute',
    left: -12,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 0,
    height: 0,
    borderRight: `12px solid ${theme.palette.secondary.main}`,
    borderTop: '12px solid transparent',
    borderBottom: '12px solid transparent',
  },
}));

const PercentageText = styled(Typography)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  fontWeight: 600,
  fontSize: '0.875rem',
  color: theme.palette.common.white,
  textShadow: `0 0 4px ${alpha(theme.palette.secondary.main, 0.8)}`,
  zIndex: 1,
}));

const GeometricMatchPercentageDisplay = ({ matchPercentage }: { matchPercentage?: number }) => {
  if (matchPercentage === undefined) return;

  const displayValue = (Math.round(matchPercentage * 100) / 100).toLocaleString(undefined, {
    minimumFractionDigits: matchPercentage < 1 && matchPercentage > 0 ? 2 : 0,
    maximumFractionDigits: 2,
  });

  return (
    <MatchPercentageContainer>
      <Typography variant='body2' fontWeight='600' color='text.secondary'>
        Match Percentage
      </Typography>
      <PercentageBar>
        <PercentageFill hasArrow={false} style={{ width: `${Math.min(matchPercentage, 100)}%` }} />
        <PercentageText>{displayValue}%</PercentageText>
      </PercentageBar>
    </MatchPercentageContainer>
  );
};

const PartMatchPercentageDisplay = ({
  forwardMatchPercentage,
  reverseMatchPercentage,
  asset1,
  asset2,
}: {
  forwardMatchPercentage?: number;
  reverseMatchPercentage?: number;
  asset1?: Asset;
  asset2?: Asset;
}) => {
  if (forwardMatchPercentage === undefined && reverseMatchPercentage === undefined) return;

  const asset1Name = asset1 ? getAssetName(asset1) : 'Top';
  const asset2Name = asset2 ? getAssetName(asset2) : 'Bottom';

  const forwardValue =
    forwardMatchPercentage === undefined
      ? undefined
      : (Math.round(forwardMatchPercentage * 100) / 100).toLocaleString(undefined, {
          minimumFractionDigits: forwardMatchPercentage < 1 && forwardMatchPercentage > 0 ? 2 : 0,
          maximumFractionDigits: 2,
        });

  const reverseValue =
    reverseMatchPercentage === undefined
      ? undefined
      : (Math.round(reverseMatchPercentage * 100) / 100).toLocaleString(undefined, {
          minimumFractionDigits: reverseMatchPercentage < 1 && reverseMatchPercentage > 0 ? 2 : 0,
          maximumFractionDigits: 2,
        });

  return (
    <MatchPercentageContainer>
      <PartMatchContainer>
        <AssetBox>
          <Thumbnail asset={asset1} size={60} noLink />
          <AssetLabel>{asset1Name}</AssetLabel>
        </AssetBox>
        <ArrowsContainer>
          {forwardMatchPercentage !== undefined && (
            <Tooltip title={`${forwardValue}% of ${asset1Name} is in ${asset2Name}`}>
              <PercentageBar>
                <PercentageFill style={{ width: `${Math.min(forwardMatchPercentage, 100)}%` }} />
                <PercentageText>{forwardValue}%</PercentageText>
              </PercentageBar>
            </Tooltip>
          )}
          {reverseMatchPercentage !== undefined && (
            <Tooltip title={`${asset1Name} makes up ${reverseValue}% of ${asset2Name}`}>
              <PercentageBar>
                <ReversePercentageFill style={{ width: `${Math.min(reverseMatchPercentage, 100)}%` }} />
                <PercentageText>{reverseValue}%</PercentageText>
              </PercentageBar>
            </Tooltip>
          )}
        </ArrowsContainer>
        <AssetBox>
          <Thumbnail asset={asset2} size={60} noLink />
          <AssetLabel>{asset2Name}</AssetLabel>
        </AssetBox>
      </PartMatchContainer>
    </MatchPercentageContainer>
  );
};

export const MatchPercentageDisplay = ({
  matchData,
  asset1,
  asset2,
}: {
  matchData?: MatchData;
  asset1?: Asset;
  asset2?: Asset;
}) => {
  if (!matchData?.searchType) return;

  const { searchType, matchPercentage, forwardMatchPercentage, reverseMatchPercentage } = matchData;

  switch (searchType) {
    case 'geometric': {
      return <GeometricMatchPercentageDisplay matchPercentage={matchPercentage} />;
    }
    case 'part': {
      return (
        <PartMatchPercentageDisplay
          asset1={asset1}
          asset2={asset2}
          forwardMatchPercentage={forwardMatchPercentage}
          reverseMatchPercentage={reverseMatchPercentage}
        />
      );
    }
    default: {
      return;
    }
  }
};
